import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PlayArrow,
  Schedule,
} from '@mui/icons-material';
import { CameraInfo, RecordingResponse, recordingApi } from '../api/recordingApi';
import { Config } from '../config/config';
import { ScheduledRecordingService } from '../service/scheduledRecording.service';

interface CameraRecordingCardProps {
  camera: CameraInfo;
  onRecordingUpdate?: (cameraId: string, recording: RecordingResponse | null) => void;
  onScheduleSuccess?: () => void;
}

const CameraRecordingCard: React.FC<CameraRecordingCardProps> = ({
  camera,
  onRecordingUpdate,
  onScheduleSuccess,
}) => {
  const [isTestRecording, setIsTestRecording] = useState(false);
  const [isSchedulingRecording, setIsSchedulingRecording] = useState(false);
  const [currentRecording, setCurrentRecording] = useState<RecordingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'recording':
        return 'error';
      case 'idle':
        return 'success';
      case 'stopping':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'recording':
        return 'Aufnahme läuft';
      case 'idle':
        return 'Bereit';
      case 'stopping':
        return 'Stoppt...';
      case 'error':
        return 'Fehler';
      default:
        return status;
    }
  };

  const handleTestRecording = async () => {
    setIsTestRecording(true);
    setError(null);
    
    try {
      const result = await recordingApi.startRecording({
        camera_id: camera.id,
        duration_seconds: 10, // 10 Sekunden Testaufnahme
        format: 'mp4',
        quality: 'medium', // Fest auf 640x360 (medium) setzen
      });
      
      setCurrentRecording(result);
      onRecordingUpdate?.(camera.id, result);
      
      // Monitor recording status and auto-complete
      const checkRecordingStatus = async () => {
        try {
          const recordings = await recordingApi.getRecordings();
          const currentRec = recordings.find((r: any) => r.id === result.id);
          
          if (currentRec && currentRec.status === 'idle') {
            // Recording completed, update UI
            setCurrentRecording(currentRec);
            setIsTestRecording(false);
            onRecordingUpdate?.(camera.id, null);
            
            // Show success message
            setError(`✅ Test-Aufnahme erfolgreich! Datei: ${currentRec.filename} (${Math.round((currentRec.file_size_bytes || 0) / 1024 / 1024 * 10) / 10} MB) - Automatisch hochgeladen`);
            
            // Clear message after 5 seconds
            setTimeout(() => {
              setError(null);
            }, 5000);
          } else if (currentRec && currentRec.status === 'recording') {
            // Still recording, check again in 2 seconds
            setTimeout(checkRecordingStatus, 2000);
          } else {
            // Unknown status or error
            setCurrentRecording(null);
            setIsTestRecording(false);
            onRecordingUpdate?.(camera.id, null);
          }
        } catch (err) {
          console.error('Failed to check recording status:', err);
          setCurrentRecording(null);
          setIsTestRecording(false);
          onRecordingUpdate?.(camera.id, null);
        }
      };
      
      // Start checking status after 2 seconds
      setTimeout(checkRecordingStatus, 2000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test recording failed');
      setIsTestRecording(false);
    }
  };

  const handleScheduleRecording = async () => {
    setIsSchedulingRecording(true);
    setError(null);
    
    try {
      const result = await ScheduledRecordingService.createScheduledRecording({
        camera_id: camera.id,
        camera_name: camera.name,
        account_id: camera.account_id || 'default-account',
        rtsp_url: camera.rtsp_url || `rtsp://test.example.com:554/${camera.id}`,
      });
      
      // Show success message with correct data structure
      const firstScheduledDay = result.data.scheduled_days?.[0];
      const dateStr = firstScheduledDay ? 
        new Date(firstScheduledDay.date).toLocaleDateString('de-DE') : 
        'heute/morgen';
      
      setError(`✅ Geplante Aufnahme erstellt! ${result.data.total_clips} Clips für ${dateStr}`);
      
      // Trigger refresh callback
      onScheduleSuccess?.();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scheduled recording failed');
    } finally {
      setIsSchedulingRecording(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  return (
    <Card sx={{ minWidth: 300, maxWidth: 400, m: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="h3" gutterBottom>
              {camera.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {camera.location}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              ID: {camera.internal_name}
            </Typography>
          </Box>
          <Chip
            label={getStatusText(camera.status)}
            color={getStatusColor(camera.status)}
            size="small"
          />
        </Box>

        {error && (
          <Alert 
            severity={error.startsWith('✅') ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {currentRecording && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Aktuelle Aufnahme
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Datei: {currentRecording.filename}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dauer: {currentRecording.duration_seconds}s
            </Typography>
            {currentRecording.file_size_bytes && (
              <Typography variant="body2" color="text.secondary">
                Größe: {formatFileSize(currentRecording.file_size_bytes)}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outlined"
            startIcon={isTestRecording ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={handleTestRecording}
            disabled={isTestRecording || isSchedulingRecording || camera.status === 'error'}
            size="small"
          >
            {isTestRecording ? 'Teste...' : 'Test (10s)'}
          </Button>

          <Button
            variant="contained"
            startIcon={isSchedulingRecording ? <CircularProgress size={16} /> : <Schedule />}
            onClick={handleScheduleRecording}
            disabled={isSchedulingRecording || isTestRecording || camera.status === 'error'}
            color="secondary"
          >
            {isSchedulingRecording ? 'Plane...' : 'Geplante Aufnahme'}
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
};

export default CameraRecordingCard; 